import { Component, EventEmitter, Output, OnInit, Input, Inject } from '@angular/core';
import { LayoutService, ScreenSize } from 'libs/portal/src/layout/layout.service';
import { UserRightsService } from 'libs/portal/src/services/user-rights/user-rights.service';
import { Endpoints } from '@huge-monorepo/portal/config';
import { GenericService } from 'libs/portal/src/services/generic/generic.service';
import { ToastrService } from 'ngx-toastr';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ICustomerGenericService, IHIPGenericService } from '@huge-monorepo/shared/interfaces';
import { Subscription } from 'rxjs';
import { Domains } from 'libs/portal/src/models/DomainInfo';
import { NavigationService } from 'libs/portal/src/services/navigation/navigation.service';
import { Routes } from '@huge-monorepo/portal/config';
import { SharedGenericSingletonService } from '@huge-monorepo/shared/services';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TitleService } from 'libs/portal/src/services/title/title.service';
import { ToolsService } from 'libs/portal/src/services/tools/tools.service';
import { LoginRequest } from 'libs/portal/src/services/user-rights/user-rights.service';
import { UntypedFormControl } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'app-change-session-modal',
  templateUrl: './change-session-modal.component.html',
  styleUrls: ['./change-session-modal.component.scss'],
})
export class ChangeSessionModalComponent implements OnInit {
  isLightMode = this.sharedGenericService.getIsLightMode();
  path = this.sharedGenericService.getPath(!this.isLightMode);
  ScreenSize = ScreenSize;
  groupAdmin = false;
  obfuscate = false;
  preferedGraphValue: string;
  modalStyle: string = this.layoutService.screenSize > this.ScreenSize.MOBILE_L ? 'auto-height' : '';
  activeIndex: number;
  groups: any[];
  totalGroups;
  currentGroup;
  currentDefaultGroup;

  currentGroupPage = 1;
  groupOrderBy = 'Name';
  groupSearch = '';
  groupSortDesc = false;
  groupTableCount = 5;
  groupTableOffset = 0;
  loading = false;
  searchQuery = new UntypedFormControl('');
  isSuperUser = false;

  constructor(
    private nav: NavigationService,
    private layoutService: LayoutService,
    private genericService: GenericService,
    private toastService: ToastrService,
    private dialogRef: MatDialogRef<ChangeSessionModalComponent>,
    private dialog: MatDialog,
    private titleService: TitleService,
    private endpoints: Endpoints,
    private tools: ToolsService,
    private auth: UserRightsService,
    private routes: Routes,
    private sharedGenericService: SharedGenericSingletonService<ICustomerGenericService | IHIPGenericService>,
    @Inject(MAT_DIALOG_DATA) public data: any  // Inject the dialog data here
  ) {
  }

  ngOnInit() {
    this.titleService.pageTitle = 'Change Session';
    this.currentGroup = this.auth.GetGroupID();
    this.currentDefaultGroup = this.auth.GetDefualtGroupID();
    this.getUserGroups(this.groupOrderBy, this.groupSearch, this.groupTableCount, this.groupTableOffset, this.groupSortDesc);
    this.isSuperUser = this.auth.GetUserAccessRoles().IsSuperUser();
    this.searchQuery.valueChanges.pipe(
      debounceTime(500) // Waits 0.5s before triggering the function
    ).subscribe(() => {
      this.submitRefresh();
    });
  }

  navigateTo(url: string | null, queryParams?: any) {
    if (!url) {
      return;
    }
    this.nav.SetActiveIndex(this.activeIndex);
    this.nav.Navigate(url, queryParams);
    this.closeModal();
  }

  getUserGroups(orderBy: string, search: string, count: number, offset: number, desc: boolean) {
    this.loading = true;
    this.groups = null;
    this.genericService.GET(this.endpoints.GET_USER_GROUPS(count, offset, search, orderBy, desc)).subscribe((res) => {
      if (res['body'] != null) {
        this.groups = res['body'];
        this.totalGroups = this.tools.ExtractPaginationFromHeaders(res['headers']);
      }
      this.loading = false;
    });
  }

  refreshTable(search: string): void {
    this.groupSearch = search ?? '';
    this.groupOrderBy = '';
    this.groupSortDesc = this.groupSortDesc;
    this.groupTableOffset = 0;
    this.currentGroupPage = 1;
    this.getUserGroups(this.groupOrderBy, this.groupSearch, this.groupTableCount, this.groupTableOffset, this.groupSortDesc);
  }

  onGroupAction(ev) {
    if (ev.title == 'Change Session') {
      this.loading = true;
      const userId = this.auth.GetUID();

      const switchObj = {
        UserID: userId,
        GroupID: ev.row.ID,
      };

      this.genericService.POST(this.endpoints.CHANGE_SESSION, switchObj).subscribe((res) => {
        if (res['body'] != null) {
          const response = res['body'];
          this.auth.ClearAccesses();
          // TODO: Change the Impersonated keys when integrating
          const loginRequest = new LoginRequest(
            response['user']['ID'],
            0,
            response['user']['UserID'],
            response['user']['Email'],
            response['user']['Name'],
            response['token'],
            response['user']['GroupID'],
            response['user']['GroupName'],
            response['user']['KeepAliveAccess'],
            response['user']['CDRAccess'],
            response['user']['MikrotikAccess'],
            response['user']['RelayAccess'],
            response['user']['UserAccessRoles'],
            response['user']['ImpersonatedUserAccessRoles'],
            response['user']['ImpersonatedUser'],
            response['user']['HugeAdmin'],
            response['user']['KeepAliveTypes'],
            response['user']['PortalReadAccess'],
            response['user']['PortalWriteAccess'],
            null,
            null,
            null,
            null,
            response['user']['TeltonikaAccess'],
            response['user']['ContractorsAccess'],
          );
          loginRequest.DefaultGroupID = response['user']['DefaultGroupID'];
          loginRequest.IsAggregated = response['user']['IsAggregated'];

          this.auth.Login(loginRequest);
          this.auth.SetSessionChanged(true);
          setTimeout(() => {
            this.loading = false;
            window.location.replace(this.routes.Home);
          });
        }
      });
    } else if (ev.title == 'Set as Default') {
      this.loading = true;

      const sendObj = {
        GroupId: ev.row.ID,
      };

      this.genericService.PUT(this.endpoints.UPDATE_USER_DEFAULT_GROUP(this.auth.GetUID()), sendObj).subscribe(() => {
        this.auth.SetDefaultGroupID(ev.row.ID);
        this.currentDefaultGroup = this.auth.GetDefualtGroupID();
        this.getUserGroups(this.groupOrderBy, this.groupSearch, this.groupTableCount, this.groupTableOffset, this.groupSortDesc);
      });
    }
  }

  getPagedGroups(ev: number) {
    this.currentGroupPage = ev;
    this.groupTableOffset = (this.currentGroupPage - 1) * this.groupTableCount;
    this.getUserGroups(this.groupOrderBy, this.groupSearch, this.groupTableCount, this.groupTableOffset, this.groupSortDesc);
  }

  clearSearch(): void {
    this.searchQuery.setValue('');
  }

  submitRefresh(): void {
    const search = this.searchQuery.value == '' ? null : this.searchQuery.value;
    this.refreshTable(search);

  }

  closeModal(): void {
    this.dialogRef.close();
  }

}
